import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { Image, Message, Loader, Button, Dropdown } from 'semantic-ui-react';
import { saveAs } from 'file-saver';
import { Canvg } from 'canvg'; // For SVG conversion

export default function AnimatedIcon({ submissionId }) {
  const { currentUser } = useAuth();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFormat, setSelectedFormat] = useState('svg');

  const formatOptions = [
    { key: 'svg', text: 'SVG', value: 'svg' },
    // { key: 'gif', text: 'GIF', value: 'gif' },
  ];

  useEffect(() => {
    const docRef = doc(db, 'submissions', submissionId);

    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        setSubmission(docSnap.data());
        setLoading(false);
      } else {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, [submissionId]);

  function sanitizeSvgContentUsingDOM(svgContent) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgContent, 'image/svg+xml');
  
    // Loop through elements with a "style" attribute and clean it
    const elementsWithStyle = doc.querySelectorAll('[style]');
    elementsWithStyle.forEach(element => {
      const style = element.getAttribute('style');
      if (style && style.includes('50%')) {
        const sanitizedStyle = style.replace(/50%/g, ''); // Remove problematic styles
        element.setAttribute('style', sanitizedStyle);
      }
    });
  
    return new XMLSerializer().serializeToString(doc);
  }
  


  const handleDownload = async () => {
    const svgUrl = submission.animatedURL;

    if (selectedFormat === 'svg') {
      // Fetch the SVG content from the URL
      try {
        const response = await fetch(svgUrl);
        if (!response.ok) {
          throw new Error('Failed to fetch SVG content.');
        }
        const svgContent = await response.text();

        // Create a Blob from the SVG content and save it
        const blob = new Blob([svgContent], { type: 'image/svg+xml' });
        saveAs(blob, `animated-icon-${submissionId}.svg`);
      } catch (error) {
        console.error('Error downloading SVG:', error);
      }
    } else if (selectedFormat === 'gif') {
      // Convert SVG to Canvas and then download as PNG or GIF
      const response = await fetch(svgUrl);
      const svgContent = await response.text();

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Use Canvg to render the SVG content directly
      const sanitizedContent = sanitizeSvgContentUsingDOM(svgContent);
      const v = Canvg.fromString(ctx, sanitizedContent);
      await v.render();


      if (selectedFormat === 'gif') {
        // For GIF, you may need a more complex process; here we save as PNG for now
        canvas.toBlob((blob) => {
          saveAs(blob, `animated-icon-${submissionId}.gif`);
        });
      }
    }
  };


  if (loading) return <Loader active />;

  if (!submission) return <Message negative>No submission found.</Message>;

  if (submission.status === 'animated') {
    return (
      <div>
        <Image src={submission.animatedURL} alt="Animated Icon" />
        <Dropdown
          placeholder="Select Format"
          selection
          options={formatOptions}
          onChange={(e, { value }) => setSelectedFormat(value)}
          value={selectedFormat}
          style={{ marginTop: '10px' }}
        />
        <Button primary onClick={handleDownload} style={{ marginTop: '10px' }}>
          Download as {selectedFormat.toUpperCase()}
        </Button>
      </div>
    );
  } else if (submission.status === 'pending') {
    return <Message info>Animation in progress...</Message>;
  } else {
    return <Message negative>Animation failed. Please try again.</Message>;
  }
}
